import React, { useState, useEffect } from "react";
import MasterTable from "../common/MasterTable";
import UserModal from "../users/UserModal";
import { axiosPostApi } from "../../helpers/interceptorServices";
import { apiList } from "../../helpers/apiList";
import CustomAlert from "../../helpers/CustomAlert";
import { toast } from "react-toastify";
import Loader from "../Loader/Loader";
import confetti from "canvas-confetti";
import AuditModal from "../AuditModal";
import UserAuditModal from "./UserAuditModal";
import UserFilterModal from "./UserFilterModal";
import UserFilterBar from "./UserFilterBar";
import BlockedUsersModal from "./BlockedUsersModal";
import HeaderWithBreadcrumb from "../common/HeaderWithBreadcrumb";
import useTableExport from "../../Hooks/useTableExport";
import useExcelDownloader from "../../Hooks/useExcelDownloader";

const defaultFilterData = {
  userId: "",
  userProfile: null,
  status: null,
  fromDate: "",
  toDate: "",
  name: "",
  emailId: "",
  mobileNo: "",
  search: "",
};

const UserMaster = () => {
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  // const pageSize = 10;
  const [pageSize, setPageSize] = useState(10);
  const loggedInUserName = sessionStorage.getItem("username");
  const [noData, setNoData] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [modalAction, setModalAction] = useState("");
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState([]);
  const [pageDetails, setPageDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [auditData, setAuditData] = useState("");
  const [filterData, setFilterData] = useState(defaultFilterData);
  const [isModalOpenFilter, setIsModalOpenFilter] = useState(false);
  const [isModalOpenAudit, setIsModalOpenAudit] = useState(false);
  const { downloadExcel } = useExcelDownloader();
  const { exportAllData, exportLoading } = useTableExport(downloadExcel);

  const handleReset = () => {
    setFilterData(defaultFilterData);
    setCurrentPage(1);
    // fetchUserData(1, defaultFilterData);
  };

  const openModalFilter = () => {
    setCurrentPage(1);
    setIsModalOpenFilter(true);
  };

  const closeModalFilter = () => {
    setIsModalOpenFilter(false);
  };

  const openModalAudit = () => {
    setIsModalOpenAudit(true);
  };

  const closeModalAudit = () => {
    setIsModalOpenAudit(false);
  };

  const handleAuditData = (data) => {
    let UserData = userData.find((item) => item?.id === data?.id);
    console.log("UserData", UserData);
    fetchUserAudit(UserData);
  };

  const fetchUserAudit = async (UserData) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,
        userCode: UserData.userId,
      };

      const response = await axiosPostApi(
        `${apiList.USER_AUDIT_DATA}`,
        requestBody,
      );
      console.log("FetchUserAudit", response);

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setAuditData(response?.data?.data?.auditDetails);
        openModalAudit(auditData);
        setLoading(false);
      } else {
        setLoading(false);
        CustomAlert({
          title: "Error",
          text: response?.data?.message || "Failed to fetch audit data.",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      });
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData(1, filterData);
  }, []);

  const fetchUserData = async (
    currentPage,
    filters = filterData,
    size = pageSize,
  ) => {
    setLoading(true);
    try {
      const requestBody = {
        username: loggedInUserName,
        userId: filters.userId || "",
        userProfile: filters.userProfile?.value || "",
        status: filters.status?.value || "",
        fromDate: formatDate(filters.fromDate),
        toDate: formatDate(filters.toDate),
        name: filters.name || "",
        emailId: filters.emailId || "",
        mobileNo: filters.mobileNo || "",
        search: filters.search || "",
      };

      const response = await axiosPostApi(
        // `${apiList.FETCH_USER_DATA}?&pageSize=${pageSize}&pageNo=${currentPage}`,
        `${apiList.FETCH_USER_DATA}?&pageSize=${size}&pageNo=${currentPage}`,
        requestBody,
      );

      if (
        response?.data?.status === true &&
        response?.data?.respCode === "00"
      ) {
        setPageDetails(response?.data?.pageDetails);
        const modifiedData = modifyStructure(response?.data?.data);
        setUserData(modifiedData);

        if (modifiedData?.length === 0) {
          setNoData(true);
        } else {
          setNoData(false);
        }
      } else {
        setNoData(true);
        setUserData([]);
        setPageDetails(null);

        // No match is a normal outcome of searching: show the empty table, no pop-up
        const noMatch =
          response?.data?.respCode === "01" || /not found/i.test(response?.data?.message || "");
        if (noMatch) {
          // nothing to report
        } else if (response?.data?.data !== null) {
          CustomAlert({
            title: "No Data",
            text: "No data available for the specified criteria.",
            icon: "info",
            confirmButtonText: "Okay",
          });
        } else {
          CustomAlert({
            title: "Failed to Fetch Data",
            text:
              response.data.message ||
              "No data available for the specified criteria.",
            icon: "error",
            confirmButtonText: "Retry",
          });
        }
      }
    } catch (error) {
      CustomAlert({
        title: "Error",
        text: "An error occurred while fetching the data. Please try again later.",
        icon: "error",
        confirmButtonText: "Retry",
      }).then((result) => {
        if (result.isConfirmed) {
          sessionStorage.clear();
          window.location.href = "/";
        }
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const [year, month, day] = dateStr.split("-");
    return `${day}-${month}-${year}`;
  };

  const modifyStructure = (response) => {
    const modified = response?.map((user, index) => ({
      ...user,
      id: index + 1,
      address: user.address1 + ", " + user.address2 + ", " + user.address3,
      roleName: user?.roleName === "Customer" ? "LMBP" : user?.roleName,
      // Add a display field for the table
      userProfileDisplay:
        user?.userProfile === "CUSTOMER"
          ? "LMBP"
          : user?.userProfile === "SUB_LMBP" ||
            user?.userProfile === "SUB-LMBP"
          ? "Sub-LMBP"
          : user?.userProfile,
    }));
    return modified;
  };

  // Authorize opens the user's details (as View does) with the confirmation and
  // an Authorize button, so the checker sees what they are approving
  const [authorizing, setAuthorizing] = useState(false);

  const handleAuthorization = (authData) => {
    const row = userData.find((item) => item?.id === authData?.id) || authData;
    setEditData(row);
    setModalAction("authorize");
    setShowModal(true);
  };

  const authorizeUser = (authData) => {
    setAuthorizing(true);
    const requestBody = {
      ...authData,
      username: loggedInUserName,
      status: "2",
    };

    axiosPostApi(apiList.AUTH_USER_DATA, requestBody)
      .then((response) => {
        if (response?.status === 200 && response?.data?.respCode === "00") {
          handleCloseModal();
          CustomAlert({
            title: "Success",
            text: "User Authorized successfully!",
            icon: "success",
            confirmButtonText: "Ok",
          });
          const canvas = document.createElement("canvas");
          canvas.style.position = "fixed";
          canvas.style.top = "0";
          canvas.style.left = "0";
          canvas.style.width = "100%";
          canvas.style.height = "100%";
          canvas.style.zIndex = "3000";
          document.body.appendChild(canvas);

          const confettiInstance = confetti.create(canvas, {
            resize: true,
            useWorker: true,
          });

          confettiInstance({
            particleCount: 250,
            spread: 100,
            origin: { y: 0.6 },
          });

          setTimeout(() => {
            document.body.removeChild(canvas);
          }, 3000);
          fetchUserData(1, filterData);
        } else {
          CustomAlert({
            title: "Failed to Authorized user",
            text:
              response.data?.message ||
              "Invalid credentials. Please try again!",
            icon: "error",
            confirmButtonText: "Retry",
          });
        }
      })
      .catch((error) => {
        console.error("Error Authorized user", error);
        toast.error("Failed to Authorized user.");
      })
      .finally(() => setAuthorizing(false));
  };

  const userHeaders = [
    "LMBP/HUB/\nFranchise Id",
    "LMBP/HUB/\nFranchise Name",
    "User ID",
    "User Name",
    "Contact No",
    "Email Id",
    "Role",
    "Profile",
    "Status",
  ];

  const userHeaderToKeyMap = {
    "LMBP/HUB/\nFranchise Id": "userCode",
    "LMBP/HUB/\nFranchise Name": "customerName",
    "User ID": "userId",
    "User Name": "name",
    "Contact No": "mobileNo",
    "Email Id": "emailId",
    Role: "roleName",
    Profile: "userProfileDisplay",
    Status: "authStatus",
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditData({});
    setModalAction("");
  };

  const handleSendCredentials = async (data) => {
    const payload = {
      username: loggedInUserName,
      customerCode: data?.userCode || "",
      userCodes: [data?.userId] || [],
    };

    try {
      const response = await axiosPostApi(apiList.SEND_CREDENTIALS, payload);
      if (response?.status === 200 && response?.data?.respCode === "00") {
        CustomAlert({
          title: "Success",
          text: response?.data?.message || `Credentials sent successfully!`,
          icon: "success",
          confirmButtonText: "Ok",
        });
        // setSelectedUsers([]);
      } else if (response?.data?.respCode === "01") {
        CustomAlert({
          title: "Data Fetch Error",
          text: response?.data?.message || "No data available",
          icon: "error",
          confirmButtonText: "Retry",
        });
      } else {
        CustomAlert({
          title: `Failed to send credentials`,
          text:
            response.data?.message || "Invalid credentials. Please try again!",
          icon: "error",
          confirmButtonText: "Retry",
        });
      }
    } catch (error) {
      console.log(`Failed to send credentials.`);
    }
  };

  const handleUser = (data, actionType = "view") => {
    console.log("::data::", data);

    setModalAction(actionType);
    if (actionType === "add") {
      setEditData({});
      setShowModal(true);
    } else if (actionType === "sendCredentials") {
      CustomAlert({
        title: "Send Credentials",
        text: "Are you sure you want to send the login credentials to the customer via email?",
        icon: "info",
        confirmButtonText: "Send",
        cancelButtonText: "Cancel",
        showCancelButton: true,
        cancelButtonColor: "#d33",
      }).then((result) => {
        if (result?.isConfirmed) {
          handleSendCredentials(data);
        }
      });
    } else {
      let UserData = userData.find((item) => item?.id === data?.id);
      if (UserData) {
        setEditData(UserData);
        setShowModal(true);
      }
    }
  };

  const handleDownloadExcel = () => {
    const requestBody = {
      username: loggedInUserName,
      userId: filterData.userId || "",
      userProfile: filterData.userProfile?.value || "",
      status: filterData.status?.value || "",
      fromDate: formatDate(filterData.fromDate),
      toDate: formatDate(filterData.toDate),
      name: filterData.name || "",
      emailId: filterData.emailId || "",
      mobileNo: filterData.mobileNo || "",
      search: filterData.search || "",
    };

    exportAllData({
      apiUrl: apiList.FETCH_USER_DATA,
      requestBody,
      headers: userHeaders,
      headerToKeyMap: userHeaderToKeyMap,
      fileName: "UserMaster.xlsx",
    });
  };

  const pageSizeCount = pageDetails === null ? 0 : pageDetails.pageSize;
  const totalRecord = pageDetails === null ? 0 : pageDetails.totalRecords;

  return (
    <div>
      <HeaderWithBreadcrumb title="User Master" breadcrumb="Master" />

      <div className="flex justify-end mb-2">
        <button
          type="button"
          className="btn btn-submit !rounded-lg"
          onClick={() => setShowBlockedUsers(true)}
        >
          Blocked Users
        </button>
      </div>
      <BlockedUsersModal
        show={showBlockedUsers}
        handleClose={() => setShowBlockedUsers(false)}
        onUnblocked={() => fetchUserData(1, filterData, pageSize)}
      />

      {/* Filters live on the page (outside the loading area) so typing keeps focus */}
      <UserFilterBar
        filters={filterData}
        onChange={(next) => {
          setFilterData(next);
          setCurrentPage(1);
          fetchUserData(1, next, pageSize);
        }}
      />
      <div className="bg-[#FFFFFF] shadow-custom rounded-[14px] px-[10px]">
        {loading ? (
          <Loader />
        ) : (
          <>
            <MasterTable
              loading={loading}
              pageDetails={pageDetails}
              headers={userHeaders}
              data={userData}
              handleAction={handleUser}
              filterFields={["name"]}
              entityType="User"
              headerToKeyMap={userHeaderToKeyMap}
              noData={noData}
              handleAuth={handleAuthorization}
              currentPage={currentPage}
              pageSize={pageSize}
              // pageSize={pageSizeCount}
              totalRecord={totalRecord}
              // fetchNextData={(page) => fetchUserData(page, filterData)}
              fetchNextData={(page, size) =>
                fetchUserData(page, filterData, size)
              }
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              handleAuditData={handleAuditData}
              openModalFilter={openModalFilter}
              handleReset={handleReset}
              filterData={filterData}
              hideFilterControls
              fetchData={() => {
                setCurrentPage(1);
                fetchUserData(1, defaultFilterData, pageSize);
              }}
              handleExport={handleDownloadExcel}
              exportLoading={exportLoading}
            />
            <UserModal
              show={showModal}
              handleClose={handleCloseModal}
              editData={editData}
              action={modalAction}
              fetchUserData={() => fetchUserData(1, filterData, pageSize)}
              onAuthorize={authorizeUser}
              authorizing={authorizing}
            />
            {isModalOpenFilter ? (
              <UserFilterModal
                show={isModalOpenFilter}
                handleClose={closeModalFilter}
                setUserData={setUserData}
                setPageDetails={setPageDetails}
                setNoData={setNoData}
                currentPage={currentPage}
                pageSize={pageSize}
                filterData={filterData}
                setFilterData={setFilterData}
                handleReset={handleReset}
              />
            ) : null}
            {isModalOpenAudit ? (
              <UserAuditModal
                show={isModalOpenAudit}
                handleClose={closeModalAudit}
                auditData={auditData}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default UserMaster;
